import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getWorkspace } from '../../../service/exams.api';
import { ingestionKeys } from '../../Exams/shared/keys';
import { normalise } from '../../ScriptIngestion/CheckPages/state/model';
import { keepNewerWorkspace } from '../../ScriptIngestion/CheckPages/state/useIngestionWorkspace';
import { isConverting } from './bulkIngestionResume';

// The bulk job, read with one GET /ingestion/{jobId} (01 K27): the Check pages
// workspace cache (ingestionKeys.workspace) is written from the same reply, so
// the converting poll keeps Check pages current without a second request. This
// query keeps what the shared workspace model doesn't: the bulk job's own
// fields (06 §3.2) — `commit` {state, submissionIds, students[{scriptId,
// studentId, submissionId, pageIds}]}, `autoSortFailed`, `mode` — plus the
// status and version the wizard steers by. A view without `commit` reads as
// commit null; committedReply.js rebuilds the reply for a COMMITTED job then.

export const bulkJobKey = (jobId) => ['ingestion', 'bulkJob', jobId];

export const POLL_MS = 2500;

/** How often a COMMITTING job is re-read while Organise waits on it. */
export const COMMIT_POLL_MS = 5000;

/** The bulk fields of a job view; never null for a reply that is a job. */
export const bulkJobOf = (view) => ({
  jobId: view?.jobId ?? null,
  status: view?.status ?? null,
  version: view?.version ?? 0,
  mode: view?.mode ?? 'AUTO',
  autoSortFailed: Boolean(view?.autoSortFailed),
  commit: view?.commit ?? null,
  counts: view?.counts ?? {},
});

/** Fetch the job and write both caches; resolves the bulk fields. */
export const fetchBulkJob = async (queryClient, jobId, { signal } = {}) => {
  const view = await getWorkspace(jobId, { signal });
  if (!view || typeof view !== 'object' || !view.jobId) {
    throw new Error("We couldn't load this upload. Try again.");
  }
  queryClient.setQueryData(ingestionKeys.workspace(jobId), (old) =>
    keepNewerWorkspace(old, normalise(view))
  );
  return bulkJobOf(view);
};

/**
 * @param {string|null} jobId
 * @param {{enabled?: boolean, poll?: boolean, watchCommit?: boolean}} [opts]
 *   poll: refetch every POLL_MS while the job is converting (explode → read →
 *   group); watchCommit: refetch every COMMIT_POLL_MS while it is COMMITTING
 *   (a claim the server's reconciler may finish)
 */
export default function useBulkIngestionJob(
  jobId,
  { enabled = true, poll = false, watchCommit = false } = {}
) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: bulkJobKey(jobId),
    queryFn: ({ signal }) => fetchBulkJob(queryClient, jobId, { signal }),
    enabled: Boolean(jobId) && enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchInterval: (data) => {
      if (
        poll &&
        (!data || isConverting(data.status) || data.status === 'OPEN')
      )
        return POLL_MS;
      if (watchCommit && data?.status === 'COMMITTING') return COMMIT_POLL_MS;
      return false;
    },
  });
}
